// Joylashuvlar reyestri (site) — `xsib-platform` `site` moduli bilan kontrakt.
//
//   GET  /api/sites?q&region&state&page&size → {rows, total, page, size}
//   GET  /api/sites/regions                  → string[]
//   GET  /api/sites/{id}                     → SiteEntry
//   POST /api/sites                          → SiteEntry
//   PUT  /api/sites/{id}                     → SiteEntry
//   POST /api/sites/{id}/state  {state}      → OperationResult
//
// Huquqlar: o'qish `site.read`, yozish `site.write`.
//
// ⚠️ O'CHIRISH ENDPOINTI YO'Q va bu ataylab. Joylashuvga boshqa modul
// (NAS, jihoz, kelajakda ariza) havola qilishi mumkin, platform esa o'sha
// jadvallarni ko'ra olmaydi — ya'ni "hech kim ishlatmayapti" degan xulosani
// chiqarib bo'lmaydi. Shuning uchun panelda ham FAQAT "yopish" bor: yozuv
// joyida qoladi, unga bog'langan havolalar buzilmaydi, lekin yangi
// biriktirishlarda tanlanmaydi.
//
// ⚠️ Sahifa 0 dan sanaladi (`/api/nas` bilan bir xil andoza), `TableState.page`
// esa 1 dan — chegara aynan shu faylda o'giriladi.

import { z } from 'zod'
import { api } from '@/shared/api/client'
import { operationResultSchema, type OperationResult } from '@/shared/api/operationResult'
import { uuidSchema } from '@/shared/api/schemas'
import type { PageResult } from '@/shared/ui/table/DataTable'
import type { TableState } from '@/shared/ui/table/tableState'

export const SITE_STATES = ['ACTIVE', 'CLOSED'] as const
export type SiteState = (typeof SITE_STATES)[number]

/**
 * ⚠️ `latitude`/`longitude` — SON yoki `null`, va IKKALASI BIRGA. Server
 * yarim koordinatani (faqat kenglik) qabul qilmaydi, chunki u xaritada
 * hech qanday nuqtani anglatmaydi. Sxema ham shuni takrorlaydi:
 * {@link coordinateError} ga qarang.
 */
const siteEntrySchema = z.object({
  id: uuidSchema,
  name: z.string(),
  region: z.string(),
  district: z.string().nullish(),
  address: z.string().nullish(),
  latitude: z.number().nullish(),
  longitude: z.number().nullish(),
  notes: z.string().nullish(),
  state: z.enum(SITE_STATES),
})

const sitePageSchema = z.object({
  rows: z.array(siteEntrySchema),
  total: z.number(),
  page: z.number(),
  size: z.number(),
})

export interface SiteEntry {
  id: string
  name: string
  region: string
  district: string | null
  address: string | null
  latitude: number | null
  longitude: number | null
  notes: string | null
  state: SiteState
}

export interface SiteDraft {
  name: string
  region: string
  district?: string | null
  address?: string | null
  latitude?: number | null
  longitude?: number | null
  notes?: string | null
}

function toSite(raw: z.infer<typeof siteEntrySchema>): SiteEntry {
  return {
    id: raw.id,
    name: raw.name,
    region: raw.region,
    district: raw.district ?? null,
    address: raw.address ?? null,
    latitude: raw.latitude ?? null,
    longitude: raw.longitude ?? null,
    notes: raw.notes ?? null,
    state: raw.state,
  }
}

export interface SiteFilter {
  region: string | null
  state: SiteState | null
}

/**
 * Ro'yxat — `DataTable` kutgan shaklda.
 *
 * Filtr yopiq (`useMemo` bilan yopib beriladi), qidiruv esa `TableState` dan
 * keladi: ikkalasi ham SERVERDA bajariladi. Mijoz tomonida filtrlash bu yerda
 * ham yaramaydi (`nasApi.ts` dagi bilan bir xil sabab) — u faqat joriy
 * sahifani ko'rib, "bunday joylashuv yo'q" degan yolg'on xulosa berardi.
 */
export function fetchSitePage(
  filter: SiteFilter,
): (state: TableState, signal: AbortSignal) => Promise<PageResult<SiteEntry>> {
  return async (state, signal) => {
    const search = state.search.trim()
    const response = await api.get<unknown>('/api/sites', {
      signal,
      params: {
        // Bo'sh qiymat UMUMAN yuborilmaydi — `nasApi.fetchNasPage` bilan bir xil
        q: search === '' ? undefined : search,
        ...(filter.region ? { region: filter.region } : {}),
        ...(filter.state ? { state: filter.state } : {}),
        page: state.page - 1,
        size: state.pageSize,
      },
    })
    const parsed = sitePageSchema.parse(response.data)
    return { items: parsed.rows.map(toSite), total: parsed.total }
  }
}

/**
 * Reyestrning to'liq nusxasi — id → nom xaritasi uchun.
 *
 * ⚠️ Bu ro'yxat KICHIK: joylashuv qo'lda kiritiladigan ma'lumotnoma
 * (viloyat/tuman darajasidagi nuqtalar), abonent yoki sessiya emas. Shuning
 * uchun uni bir so'rovda olib keshda saqlash to'g'ri — har jadval qatori
 * uchun alohida `GET /api/sites/{id}` yuborish N+1 bo'lardi.
 *
 * `INDEX_SIZE` ataylab katta: agar reyestr shundan ham oshsa, xarita CHALA
 * bo'lib qoladi va nomi topilmagan joylashuv "noma'lum" bo'lib ko'rinardi —
 * shuning uchun oshib ketganini {@link SiteIndex} `truncated` orqali
 * bildiradi va UI buni yashirmaydi.
 */
const INDEX_SIZE = 500

export interface SiteIndex {
  sites: readonly SiteEntry[]
  /** Reyestr `INDEX_SIZE` dan katta — xarita chala, "noma'lum" xulosasiga ishonib bo'lmaydi */
  truncated: boolean
}

export async function fetchSiteIndex(): Promise<SiteIndex> {
  const response = await api.get<unknown>('/api/sites', { params: { page: 0, size: INDEX_SIZE } })
  const parsed = sitePageSchema.parse(response.data)
  return { sites: parsed.rows.map(toSite), truncated: parsed.total > parsed.rows.length }
}

/**
 * Mavjud viloyat nomlari.
 *
 * ⚠️ BU REYESTRNING BUTUN MA'NOSI. Viloyat — erkin matn, va u hisobotda
 * guruhlash kaliti bo'lib xizmat qiladi ("Toshkent" va "toshkent" ikki
 * alohida qator bo'lib chiqadi). Server tayyor ro'yxatni shuning uchun
 * beradi: operator yozmaydi, TANLAYDI.
 */
export async function fetchSiteRegions(): Promise<string[]> {
  const response = await api.get<unknown>('/api/sites/regions')
  return z.array(z.string()).parse(response.data)
}

export async function fetchSite(id: string): Promise<SiteEntry> {
  const response = await api.get<unknown>(`/api/sites/${id}`)
  return toSite(siteEntrySchema.parse(response.data))
}

export async function createSite(draft: SiteDraft): Promise<SiteEntry> {
  const response = await api.post<unknown>('/api/sites', draft)
  return toSite(siteEntrySchema.parse(response.data))
}

export async function updateSite(id: string, draft: SiteDraft): Promise<SiteEntry> {
  const response = await api.put<unknown>(`/api/sites/${id}`, draft)
  return toSite(siteEntrySchema.parse(response.data))
}

/**
 * Yopish / qayta faollashtirish.
 *
 * ⚠️ `OperationResult` — kontraktda AYNAN shunday (`POST /api/sites/{id}/state`).
 * Bu yerda `PARTIAL` amalda kutilmaydi (faqat baza yozuvi, CoA yo'q), lekin
 * natija baribir `OperationResultAlert` orqali ko'rsatiladi: shaklni "har doim
 * OK" deb soddalashtirish kelajakdagi `PARTIAL` ni jimgina yutib yuborardi
 * (`nasApi.setNasSecret` bilan bir xil sabab).
 */
export async function setSiteState(id: string, state: SiteState): Promise<OperationResult> {
  const response = await api.post<unknown>(`/api/sites/${id}/state`, { state })
  return operationResultSchema.parse(response.data)
}

// --- Mijoz tomonidagi tekshiruv ---------------------------------------------

export const LATITUDE_MIN = -90
export const LATITUDE_MAX = 90
export const LONGITUDE_MIN = -180
export const LONGITUDE_MAX = 180

/** Koordinata xatosining turi — matn UI tomonda tarjima qilinadi */
export type CoordinateError = 'latRange' | 'lonRange' | 'incomplete'

/**
 * Koordinatalarni tekshiradi — SERVERDAGI qoidaning aynan takrori.
 *
 * ⚠️ NEGA MIJOZDA TAKRORLANADI: yarim koordinata (faqat kenglik yozilgan)
 * jimgina noto'g'ri emas — u xaritada NUQTA EMAS. Server uni rad etadi, lekin
 * o'shanda operator to'ldirgan butun forma qaytarilardi. Bu yerda tekshirish
 * server tekshiruvini almashtirmaydi, faqat uni oldinroq aytadi.
 *
 * `null` — "koordinata yo'q", bu TO'G'RI holat: joylashuvning aniq nuqtasi
 * hali o'lchanmagan bo'lishi mumkin.
 */
export function coordinateError(
  latitude: number | null | undefined,
  longitude: number | null | undefined,
): CoordinateError | null {
  // Shartlar ATAYLAB yoyib yozilgan (`hasLat !== hasLon` emas): shu ko'rinishda
  // TypeScript ikkala qiymatni ham `number` ga toraytiradi va quyidagi
  // solishtirishlar tip tekshiruvidan o'tadi.
  if (latitude === null || latitude === undefined) {
    return longitude === null || longitude === undefined ? null : 'incomplete'
  }
  if (longitude === null || longitude === undefined) return 'incomplete'
  if (latitude < LATITUDE_MIN || latitude > LATITUDE_MAX) return 'latRange'
  if (longitude < LONGITUDE_MIN || longitude > LONGITUDE_MAX) return 'lonRange'
  return null
}

/**
 * Tashqi xaritaga havola (OpenStreetMap).
 *
 * ⚠️ XARITA KUTUBXONASI QO'SHILMAYDI. Loyihada birorta chizish/xarita
 * bog'liqligi yo'q (`package.json`) va uni bitta ustun uchun qo'shish
 * ALOHIDA qaror: build hajmi, plitka serveri, litsenziya, maxfiylik.
 * Tashqi havola esa xuddi shu savolga javob beradi — "bu nuqta qayerda?" —
 * va hech narsa qo'shmaydi.
 */
export function mapLink(latitude: number, longitude: number): string {
  return `https://www.openstreetmap.org/?mlat=${latitude}&mlon=${longitude}#map=17/${latitude}/${longitude}`
}

/** Koordinatani matn ko'rinishida — 5 kasr (≈1 m aniqlik, ortig'i shovqin) */
export function formatCoordinates(latitude: number, longitude: number): string {
  return `${latitude.toFixed(5)}, ${longitude.toFixed(5)}`
}
